<template>
	<div>
		<!-- Users List header -->
		<a-row type="flex" :gutter="24">
			<a-col :span="12" class="mb-24">
				<a-button type="primary" @click="$router.push('/users/create')" style="margin-right: 8px;">New User</a-button>
		
			</a-col>
			<a-col :span="12" class="mb-24 text-right">
				<!-- WebSocket connection status indicator -->
				<ConnectionStatus :show-details="true" />
			</a-col>
		</a-row>
		<!-- / Users List header -->

		<!-- Users List card -->
		<a-card :bordered="false" class="header-solid mb-24" :bodyStyle="{ padding: 0, paddingTop: '16px' }">
			<!-- Table search -->
			<div class="mx-25">
				<a-row type="flex" :gutter="24">
					<a-col :span="24" class="text-right">

						<a-input-search placeholder="input search text" style="max-width: 200px;" v-model="query"
							@input="debouncedSearch" />
					</a-col>
				</a-row>
			</div>
			<!-- / Table search -->

			<!-- Users table -->
			<a-table class="mt-20" :columns="columns" :data-source="users" rowKey="id"
				:pagination="paginationConfig"
				:loading="isNotLoading"
				@change="handleTableChange">
				<template slot="action" slot-scope="text, record">
					<a-space>
						<a-button type="primary" @click="$router.push(`/users/edit/${record.id}`)">
							<a-icon type="edit" />
						</a-button>
						<a-button type="default" @click="showUserLoads(record)">
							<a-icon type="eye" />
						</a-button>
						<a-popconfirm
							:title="record.is_active ? 'Are you sure you want to deactivate this user?' : 'Are you sure you want to activate this user?'"
							ok-text="Yes"
							cancel-text="No"
							@confirm="toggleUserStatus(record)"
						>
							<a-button 
								:type="record.is_active ? 'danger' : 'success'" 
								:loading="togglingUserId === record.id"
								:title="record.is_active ? 'Deactivate' : 'Activate'"
							>
								<a-icon :type="record.is_active ? 'stop' : 'play-circle'" />
							</a-button>
						</a-popconfirm>
						<a-popconfirm
							title="Are you sure you want to delete this user? This action cannot be undone."
							ok-text="Yes"
							cancel-text="No"
							@confirm="deleteUser(record)"
						>
							<a-button 
								type="danger" 
								:loading="deletingUserId === record.id"
								title="Delete User"
							>
								<a-icon type="delete" />
							</a-button>
						</a-popconfirm>
					</a-space>
				</template>
				<!-- Created At Column -->
				<template slot="created_at" slot-scope="created_at">
					{{ formatDate(created_at) }}
				</template>
				<!-- Company Column -->
				<template slot="company" slot-scope="company">
					{{ company ? company.name : 'No Company' }}
				</template>
			</a-table>
			<!-- / Users table -->

		</a-card>
		<!-- / Users List card -->

		<!-- User Loads Popup -->
		<UserLoadsPopup
			:visible="userLoadsPopupVisible"
			:user-id="selectedUserId"
			:user-info="selectedUserInfo"
			@close="closeUserLoadsPopup"
		/>


	</div>
</template>

<script>
import debounce from 'lodash/debounce';
import userAPI from '../../api/user';
import ConnectionStatus from '../../components/WebSocket/ConnectionStatus.vue';
import UserLoadsPopup from '../../components/Users/UserLoadsPopup.vue';


// Table columns
const columns = [
	{
		title: 'FULL NAME',
		dataIndex: 'full_name',
		width: 140,
		ellipsis: true,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
	},
	{
		title: 'EMAIL',
		dataIndex: 'email',
		width: 180,
		ellipsis: true,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
	},
	{
		title: 'PHONE NUMBER',
		dataIndex: 'phone_number',
		width: 140,
		ellipsis: true,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
	},
	{
		title: 'COMPANY',
		dataIndex: 'company',
		width: 140,
		ellipsis: true,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
		scopedSlots: { customRender: 'company' },
	},
	{
		title: 'CREATED AT',
		dataIndex: 'created_at',
		width: 140,
		sorter: true,
		sortDirections: ['descend', 'ascend'],
		scopedSlots: { customRender: 'created_at' },
	},
	{
		title: 'ACTION',
		dataIndex: 'action',
		scopedSlots: { customRender: 'action' },
		width: 280,
		fixed: 'right',
	},
];

export default {
	components: {
		ConnectionStatus,
		UserLoadsPopup
	},
	data() {
		return {
			// Table columns
			columns,

			// Table Users
			users: [],

			// Table page size
			pageSize: 10,

			// Table page skip
			currentPage: 1,

			totalCount: 0,

			// Table search query
			query: '',



			isNotLoading: false,

			debouncedSearch: () => { },

			// Sorting state
			sortBy: null,
			sortOrder: null,
			
			// User loads popup state
			userLoadsPopupVisible: false,
			selectedUserId: null,
			selectedUserInfo: {},
			
			// Toggle loading state
			togglingUserId: null,
			
			// Delete loading state
			deletingUserId: null,


		}
	},
	computed: {
		// Pagination configuration with validation
		paginationConfig() {
			return {
				pageSize: this.pageSize || 10,
				current: this.currentPage || 1,
				total: this.totalCount || 0,
				onChange: this.handlePageChange,
				showSizeChanger: true,
				showQuickJumper: true,
				showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
			};
		},

		// CSV data array
		csvData() {
			return this.users.map(item => ({
				"ID": item.id,
				"Full Name": item.full_name,
				"Email": item.email,
				"Phone Number": item.phone_number,
				"Company": item.company ? item.company.name : 'No Company',
				"Created At": item.created_at,
			}));
		}
	},
	methods: {
		// User loads popup methods
		showUserLoads(user) {
			this.selectedUserId = user.id;
			this.selectedUserInfo = user;
			this.userLoadsPopupVisible = true;
		},
		
		closeUserLoadsPopup() {
			this.userLoadsPopupVisible = false;
			this.selectedUserId = null;
			this.selectedUserInfo = {};
		},

		// Register link popup methods

		
		// Handle table changes (pagination, filters, sorting)
		// Handle table changes (pagination, filters, sorting)
		async handleTableChange(pagination, filters, sorter) {
			// Update pagination
			if (pagination.current !== this.currentPage) {
				this.currentPage = pagination.current;
			}
			if (pagination.pageSize !== this.pageSize) {
				this.pageSize = pagination.pageSize;
				this.currentPage = 1; // Reset to first page when page size changes
			}

			// Update sorting
			if (sorter && sorter.columnKey) {
				this.sortBy = sorter.columnKey;
				this.sortOrder = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;
			} else if (sorter && sorter.field) {
				this.sortBy = sorter.field;
				this.sortOrder = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : null;
			} else {
				this.sortBy = null;
				this.sortOrder = null;
			}

			// Fetch data with new parameters
			await this.fetchUsers();
		},



		async handlePageChange(page) {
			this.currentPage = page;
			await this.fetchUsers();
		},

		// Centralized method to fetch users with all parameters
		async fetchUsers() {
			this.isNotLoading = true;
			
			const params = {
				page_size: this.pageSize,
				page: this.currentPage,
			};

			// Add search query if exists
			if (this.query && this.query.length >= 3) {
				params.query = this.query;
			}

			// Add sorting parameters if exists
			if (this.sortBy && this.sortOrder) {
				params.sort_by = this.sortBy;
				params.sort_order = this.sortOrder;
			}

			try {
				const response = await userAPI.getUsers({ params });
				this.users = response.data || [];
				this.totalCount = response.pagination ? response.pagination.total : 0;
			} catch (error) {
				console.error('Error fetching users:', error);
				this.users = [];
				this.totalCount = 0;
			} finally {
				this.isNotLoading = false;
			}
		},

		// Event listener for input change on table search field.
		async onSearchChange() {
			this.currentPage = 1; // Reset to first page when searching
			await this.fetchUsers();
		},



		// Export table in CSV format.
		csvExport(arrData) {
			let csvContent = "data:text/csv;charset=utf-8,";
			csvContent += [
				Object.keys(arrData[0]).join("|"),
				...arrData.map(item => Object.values(item).join("|"))
			]
				.join("\n")
				.replace(/(^\[)|(\]$)/gm, "");

			const data = encodeURI(csvContent);
			const link = document.createElement("a");
			link.setAttribute("href", data);
			link.setAttribute("download", "users-export.csv");
			link.click();
		},

		// Format date for display
		formatDate(dateString) {
			if (!dateString) return '';
			const date = new Date(dateString);
			return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
		},

		// Initialize WebSocket connection for users
		initWebSocketUsers() {
			// Subscribe to users channel
			this.$wsSubscribe('users');
			
			// Set up event listeners using global WebSocket
			this.$wsOnChannel('users', 'new_user', this.onNewUser);
			this.$wsOnChannel('users', 'user_updated', this.onUserUpdated);
			this.$wsOnChannel('users', 'user_deleted', this.onUserDeleted);
			
			// Listen to connection events
			this.$wsOn('connected', this.onWebSocketConnected);
			this.$wsOn('disconnected', this.onWebSocketDisconnected);
		},

		// WebSocket event handlers
		onWebSocketConnected(event) {
			console.log('WebSocket connected - users subscribed');
			this.$message.success('Real-time updates connected');
		},

		onWebSocketDisconnected(event) {
			console.log('WebSocket disconnected');
			this.$message.warning('Real-time updates disconnected');
		},

		// Handle new user received via WebSocket
		onNewUser(data) {
			console.log('New user received:', data);
			
			if (data.user) {
				// Add new user to the beginning of the list if we're on the first page
				if (this.currentPage === 1) {
					this.users.unshift(data.user);
					
					// If we exceed the page size, remove the last item
					if (this.users.length > this.pageSize) {
						this.users.pop();
					}
				}
				
				// Update total count
				this.totalCount += 1;
				
				// Show notification
				this.$message.info(`New user added: ${data.user.full_name}`);
			}
		},

		// Handle user update received via WebSocket
		onUserUpdated(data) {
			console.log('User updated:', data);
			
			if (data.user) {
				const index = this.users.findIndex(user => user.id === data.user.id);
				if (index !== -1) {
					// Update the user in place
					this.$set(this.users, index, data.user);
					this.$message.info(`User updated: ${data.user.full_name}`);
				}
			}
		},

		// Handle user deletion received via WebSocket
		onUserDeleted(data) {
			console.log('User deleted:', data);
			
			if (data.user_id) {
				const index = this.users.findIndex(user => user.id === data.user_id);
				if (index !== -1) {
					this.users.splice(index, 1);
					this.totalCount = Math.max(0, this.totalCount - 1);
					this.$message.info(`User deleted: ${data.full_name || data.user_id}`);
				}
			}
		},

		// Clean up WebSocket listeners
		cleanupWebSocketUsers() {
			// Unsubscribe from users channel
			this.$wsUnsubscribe('users');
		},
		
		// Toggle user status (activate/deactivate)
		async toggleUserStatus(user) {
			this.togglingUserId = user.id;
			try {
				if (user.is_active) {
					await userAPI.deactivateUser(user.id);
					this.$message.success('User deactivated successfully');
					// Update local data
					user.is_active = false;
				} else {
					await userAPI.activateUser(user.id);
					this.$message.success('User activated successfully');
					// Update local data
					user.is_active = true;
				}
			} catch (error) {
				this.$message.error(error.response?.data?.error || 'Failed to update user status');
			} finally {
				this.togglingUserId = null;
			}
		},
		
		// Delete user
		async deleteUser(user) {
			this.deletingUserId = user.id;
			try {
				await userAPI.deleteUser(user.id);
				this.$message.success('User deleted successfully');
				// Remove user from local data
				const index = this.users.findIndex(u => u.id === user.id);
				if (index !== -1) {
					this.users.splice(index, 1);
					this.totalCount = Math.max(0, this.totalCount - 1);
				}
			} catch (error) {
				this.$message.error(error.response?.data?.error || 'Failed to delete user');
			} finally {
				this.deletingUserId = null;
			}
		},
	},
	async mounted() {
		this.debouncedSearch = debounce(this.onSearchChange, 300);
		
		// Load initial data
		await this.fetchUsers();

		// Initialize WebSocket connection for real-time updates
		this.initWebSocketUsers();
	},

	beforeDestroy() {
		// Clean up WebSocket subscription when component is destroyed
		this.cleanupWebSocketUsers();
	}
}
</script>

<style lang="scss" scoped>
.table-avatar-info {
	display: flex;
	align-items: center;
}

.table-avatar-info .ant-avatar {
	margin-right: 8px;
}

// Using vuejs "Deep Selectors"
.table-avatar-info::v-deep .ant-avatar-string {
	font-size: 12px;
}

.btn-status::v-deep .anticon {
	line-height: 0;
}
</style>
